import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { PageHeader, EmptyState } from "../components/admin/AdminUI";

const BASE_CLASSES = [
  { code: "OT",   name: "Old Testament Survey" },
  { code: "NT",   name: "New Testament Survey" },
  { code: "BD",   name: "Basic Doctrine" },
  { code: "BLC",  name: "Biblical Leadership and Communication" },
  { code: "BCMF", name: "Biblical Counseling + Marriage and Family" },
  { code: "HH",   name: "Hermeneutic and Homiletic" },
  { code: "CBH",  name: "Church in The Bible and History" },
  { code: "CBD",  name: "Covenants and Biblical Discipleship" },
  { code: "BWG",  name: "Biblical Worship and Gatherings" },
  { code: "MMM",  name: "Mission and Marketplace Ministry" },
];

const ClassSchedulePage = ({ setLocationAdmin }) => {
  const navigate = useNavigate();
  const [errorMe, setErrorMe] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dates, setDates] = useState({});
  const [names, setNames] = useState({});

  const getMe = async () => {
    try {
      await axios.get("/meAdmin");
    } catch {
      setErrorMe(true);
    }
  };

  const fetchSchedules = async () => {
    try {
      const res = await axios.get("/classschedules");
      const map = {};
      const nameMap = {};
      res.data.forEach((s) => {
        map[s.code] = s.dateText;
        if (s.className) nameMap[s.code] = s.className;
      });
      setDates(map);
      setNames(nameMap);
    } catch {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    setLocationAdmin({ pathname: "/classschedulepage" });
    getMe();
    fetchSchedules();
  }, []);

  useEffect(() => {
    if (errorMe) {
      Swal.fire({
        title: "Session Expired",
        text: "Please login to your account!",
        icon: "warning",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
      navigate("/loginAdmin");
    }
  }, [errorMe, navigate]);

  const handleChange = (code, value) => {
    setDates((prev) => ({ ...prev, [code]: value }));
  };

  const handleNameChange = (code, value) => {
    setNames((prev) => ({ ...prev, [code]: value }));
  };

  const handleSave = async () => {
    const schedules = BASE_CLASSES.map(({ code }) => ({
      code,
      dateText: dates[code] || "",
      className: names[code] || "",
    }));
    setSaving(true);
    try {
      await axios.patch("/classschedules", { schedules });
      Swal.fire({
        title: "Saved!",
        text: "Class schedules have been updated. Changes will reflect on the student page immediately.",
        icon: "success",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
        confirmButtonText: "Ok",
      });
    } catch (err) {
      Swal.fire({
        text: err.response?.data?.msg || "Failed to save schedules.",
        icon: "error",
        background: "white",
        color: "black",
        confirmButtonColor: "#fb3104",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="adm-main">
      <PageHeader
        title="Class Schedule"
        subtitle="Edit the name and/or date for each class. Leave a field blank to keep the default. Changes appear on the student-facing pages."
        actions={
          <button type="button" className="adm-btn adm-btn--primary" onClick={handleSave} disabled={saving || isLoading}>
            <i className="bi bi-check2"></i>
            {saving ? "Saving..." : "Save All Changes"}
          </button>
        }
      />

      <div className="adm-card adm-card--flush">
        {isLoading ? (
          <EmptyState icon="bi-hourglass" title="Loading schedule…" />
        ) : (
          <div className="adm-table-wrap">
            <table className="adm-table" style={{ minWidth: "640px" }}>
              <thead>
                <tr>
                  <th style={{ width: "120px" }}>Code</th>
                  <th>Class Name</th>
                  <th>Schedule / Date</th>
                </tr>
              </thead>
              <tbody>
                {BASE_CLASSES.map(({ code, name }) => (
                  <tr key={code}>
                    <td>
                      <span className="adm-badge adm-badge--primary">{code}</span>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        value={names[code] || ""}
                        onChange={(e) => handleNameChange(code, e.target.value)}
                        placeholder={name}
                        aria-label={`${code} class name`}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="form-control"
                        value={dates[code] || ""}
                        onChange={(e) => handleChange(code, e.target.value)}
                        placeholder="e.g. 8-12 Sept"
                        aria-label={`${code} schedule`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassSchedulePage;
